import { ChevronDown, ChevronUp, ArrowLeft, ArrowRight } from 'lucide-react';

const WEIGHT_LABELS = { 1: 'Nice to Have', 2: 'Important', 3: 'Must Have' };
const PILL_BASE     = 'text-xs px-2 py-0.5 rounded-full border transition-all cursor-pointer select-none';
const PILL          = { 1: 'border-slate-600 text-slate-400 hover:border-slate-400', 2: 'border-amber-700 text-amber-500 hover:border-amber-400', 3: 'border-red-800 text-red-500 hover:border-red-400' };
const PILL_ACTIVE   = { 1: 'border-slate-400 bg-slate-600 text-white', 2: 'border-amber-500 bg-amber-700 text-white', 3: 'border-red-500 bg-red-700 text-white' };

/**
 * Step 2 — Feature selection grid.
 * @param {{ template, featureState, onToggle, onSetWeight, onToggleExpand, canProceed, onProceed, onBack }} props
 */
export function FeatureGrid({ template, featureState, onToggle, onSetWeight, onToggleExpand, canProceed, onProceed, onBack }) {
  const count = featureState.selectedIds.size;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base md:text-lg font-black text-white mb-1">{template.meta.title}</h2>
        <p className="text-slate-400 text-sm mb-4">{template.meta.intro_text}</p>
        <div className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
          ${canProceed ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-800'
          : count > 5  ? 'bg-red-900/40 text-red-400 border border-red-800'
                       : 'bg-amber-900/30 text-amber-400 border border-amber-800'}`}
          data-testid="feature-selection-status">
          {count === 0 && 'Select 3 to 5 features that matter most to you'}
          {count > 0 && count < 3 && `${count} selected — pick ${3 - count} more to continue`}
          {canProceed && `${count} features selected — ready to continue`}
          {count > 5 && `${count} selected — please deselect ${count - 5} (max is 5)`}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {template.features.map(f => {
          const isSelected = featureState.selectedIds.has(f.id);
          const isExpanded = featureState.expanded.has(f.id);
          const w = featureState.weights[f.id] ?? f.default_weight;
          return (
            <div key={f.id} data-testid={`feature-card-${f.id}`}
              className={`rounded-xl border p-4 transition-all duration-200
                ${isSelected ? 'bg-emerald-950/40 border-emerald-600 shadow-md' : 'bg-slate-800 border-slate-700 hover:border-slate-500'}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  {f.is_vendor_sponsored && <span className="text-xs bg-amber-950/60 text-amber-400 border border-amber-800/60 px-1.5 py-0.5 rounded mr-1.5 font-semibold">Sponsored</span>}
                  <span className="text-sm font-semibold text-white leading-snug">{f.title}</span>
                </div>
                <button type="button" data-testid={`toggle-feature-${f.id}`}
                  aria-label={isSelected ? `Remove ${f.title}` : `Include ${f.title}`}
                  aria-pressed={isSelected}
                  onClick={() => onToggle(f.id)}
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center text-xs font-bold transition-all
                    ${isSelected ? 'bg-emerald-600 border-emerald-500 text-white' : 'border-slate-500 hover:border-emerald-500 text-transparent'}`}>
                  {isSelected ? '✓' : ''}
                </button>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-3">{f.description}</p>
              {isExpanded && (
                <p className="text-xs text-slate-300 italic bg-slate-700/50 rounded-lg p-3 mb-3 leading-relaxed border-l-2 border-emerald-700">
                  "{f.adventure_text}"
                </p>
              )}
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 flex-wrap">
                  {[1, 2, 3].map(n => (
                    <button key={n} type="button" data-testid={`weight-${f.id}-${n}`}
                      aria-label={`Set weight to ${WEIGHT_LABELS[n]}`}
                      aria-pressed={w === n}
                      onClick={() => onSetWeight(f.id, n)}
                      className={`${PILL_BASE} ${w === n ? PILL_ACTIVE[n] : PILL[n]}`}>
                      {WEIGHT_LABELS[n]}
                    </button>
                  ))}
                </div>
                <button type="button" data-testid={`toggle-story-${f.id}`} onClick={() => onToggleExpand(f.id)}
                  aria-label={isExpanded ? 'Hide story' : 'Show story'}
                  aria-expanded={isExpanded}
                  className="text-slate-500 hover:text-slate-300 flex items-center gap-1 text-xs flex-shrink-0">
                  {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Story
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3">
        <button type="button" data-testid="features-back-button" onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" data-testid="proceed-to-competitors" disabled={!canProceed} onClick={onProceed}
          className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all
            ${canProceed ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}>
          Continue with {count} feature{count !== 1 ? 's' : ''} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
